import { FormValidator } from "../formValidator";
import { ValidationError, ValidationErrors } from "src/errors";
import { CreatePalletFormDataKey, RelabelCaseRecord } from "src/types";
import {
  ANIMAL_NON_CERTIFIED_MIX_ERROR_MESSAGE,
  ANIMAL_NON_CERTIFIED_VENDOR_MIX_ERROR_MESSAGE,
  ANIMAL_SKU_MIX_ERROR_MESSAGE
} from "src/constants";

export class CreatePalletFormValidator extends FormValidator {
  private static validateSelectedCases(value: unknown): ValidationError<CreatePalletFormDataKey> | undefined {
    const cases = value as RelabelCaseRecord[];
    if (!cases || cases.length === 0) {
      return new ValidationError("At least one case must be assigned to the pallet", "selectedCases");
    }
    const firstAnimalNonCertified = cases[0].isAnimalNonCertified ?? false;
    if (cases.some((c) => c.isAnimalSku !== cases[0].isAnimalSku)) {
      return new ValidationError(ANIMAL_SKU_MIX_ERROR_MESSAGE, "selectedCases");
    }
    if (cases.some((c) => (c.isAnimalNonCertified ?? false) !== firstAnimalNonCertified)) {
      return new ValidationError(ANIMAL_NON_CERTIFIED_MIX_ERROR_MESSAGE, "selectedCases");
    }
    if (firstAnimalNonCertified) {
      const vendorIds = new Set(cases.map((c) => c.vendorId).filter((id): id is string => !!id));
      if (vendorIds.size > 1) {
        return new ValidationError(ANIMAL_NON_CERTIFIED_VENDOR_MIX_ERROR_MESSAGE, "selectedCases");
      }
    }
    return undefined;
  }

  private static validateLocationId(value: unknown): ValidationError<CreatePalletFormDataKey> | undefined {
    if (typeof value !== "string" || value.trim() === "") {
      return new ValidationError("Pallet location is required", "locationId");
    }
    return undefined;
  }

  private static validateRequiredDimension(
    value: unknown,
    fieldName: CreatePalletFormDataKey,
    label: string
  ): ValidationError<CreatePalletFormDataKey> | undefined {
    if (value === undefined || value === null || value === "") {
      return new ValidationError(`${label} is required`, fieldName);
    }
    const num = Number(value);
    if (isNaN(num) || num <= 0) {
      return new ValidationError(`${label} must be a positive number`, fieldName);
    }
    return undefined;
  }

  private static readonly validationStrategies: Record<
    CreatePalletFormDataKey,
    (value: unknown) => ValidationError<CreatePalletFormDataKey> | undefined
  > = {
    selectedCases: CreatePalletFormValidator.validateSelectedCases,
    locationId: CreatePalletFormValidator.validateLocationId,
    weight: (value) => CreatePalletFormValidator.validateRequiredDimension(value, "weight", "Weight"),
    length: (value) => CreatePalletFormValidator.validateRequiredDimension(value, "length", "Length"),
    width: (value) => CreatePalletFormValidator.validateRequiredDimension(value, "width", "Width"),
    height: (value) => CreatePalletFormValidator.validateRequiredDimension(value, "height", "Height")
  };

  static validateCreatePalletFormData<T extends CreatePalletFormDataKey>(
    formData: Partial<Record<T, unknown>>
  ): ValidationErrors<T> {
    return super.validateFormData(formData, CreatePalletFormValidator.validationStrategies as never);
  }
}
